import { Heart, Baby, Flower2, ShieldCheck } from "lucide-react";
import { WOMENS_TOPICS, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";

const ICONS = [Baby, Flower2, Heart, ShieldCheck];

export default function WomensHeartHealth() {
  return (
    <>
      <PageHero
        crumb="Women's Heart"
        eyebrow="Women's Heart Health"
        titleLines={["Specialized", "Women's Heart Care", "in Varanasi"]}
        subtitle="Addressing the unique cardiac needs of women — with expertise, empathy and empowerment."
      />

      {/* Understanding */}
      <section className="pb-24 md:pb-28 bg-mint/50" data-testid="womens-understanding">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-4 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Understanding Female Heart Health</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink font-light text-balance mb-6">
                Heart disease shows up differently in women.
              </h2>
              <p className="text-lg text-sage leading-relaxed">
                Symptoms, risk factors and warning signs can differ significantly between women and men — and are too often overlooked. Dr. Mishra brings dedicated focus to women&rsquo;s cardiac care, ensuring your concerns are truly heard and your risk is assessed on your terms.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "Pregnancy", v: "Specialised monitoring" },
                  { k: "Menopause", v: "Proactive planning" },
                  { k: "Diabetes", v: "Higher vigilance" },
                  { k: "Hypertension", v: "Tailored control" },
                ].map((s) => (
                  <div key={s.k} className="rounded-3xl bg-surface border border-line p-6">
                    <div className="font-display text-2xl text-brand">{s.k}</div>
                    <div className="text-sm text-sage mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Topics across life stages */}
      <section className="py-24 md:py-32 bg-paper" data-testid="womens-topics">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Care Across Every Life Stage</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light max-w-3xl mb-14">
              Cardiac care that meets you where you are.
            </h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {WOMENS_TOPICS.map((t, i) => {
              const TIcon = ICONS[i] || Heart;
              return (
                <StaggerItem key={t.title}>
                  <div className="group rounded-3xl border border-line bg-surface p-8 h-full transition-colors duration-300 hover:border-brand">
                    <span className="grid place-items-center w-14 h-14 rounded-2xl bg-mint text-brand mb-6 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <TIcon size={24} />
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl text-ink mb-3">{t.title}</h3>
                    <p className="text-sm md:text-base text-sage leading-relaxed">{t.body}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Prevention band */}
      <section className="py-20 md:py-28 bg-brand-deep text-white grain relative overflow-hidden" data-testid="womens-prevention">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl font-light leading-tight text-balance">
                Prevention & early detection, tailored to women.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-lg text-mint/80 leading-relaxed">
                From personalised screening to lifestyle guidance, the focus is always on empowering you with clarity, confidence and a plan that fits your life.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <FAQSection items={FAQS.womens} heading="Women's Heart Health — FAQs" />
      <CTASection title="Schedule Your Women's Heart Health Consultation" />
    </>
  );
}
